import axiosInstance from '@/core/api'
import type {
	BrokeFlexMessageCampaignModel,
	BrokeFlexMessageCampaignListResponse,
	BrokeFlexMessageCampaignResponse,
	CreateBrokeFlexMessageCampaignRequest,
	UpdateBrokeFlexMessageCampaignRequest,
	BrokeFlexAnalyticsListResponse,
	BrokeFlexAnalyticsResponse,
} from '../../types'

export class BrokeFlexMessageService {
	static async getCampaigns(
		isActive?: boolean,
	): Promise<BrokeFlexMessageCampaignModel[]> {
		const { data } =
			await axiosInstance.get<BrokeFlexMessageCampaignListResponse>(
				'/admin/broke-flex-messages/campaigns',
				{ params: { isActive } },
			)
		return data.data
	}

	static async getCampaign(id: string): Promise<BrokeFlexMessageCampaignModel> {
		const { data } = await axiosInstance.get<BrokeFlexMessageCampaignResponse>(
			`/admin/broke-flex-messages/campaigns/${id}`,
		)
		return data.data
	}

	static async createCampaign(
		dto: CreateBrokeFlexMessageCampaignRequest,
	): Promise<BrokeFlexMessageCampaignModel> {
		const { data } = await axiosInstance.post<BrokeFlexMessageCampaignResponse>(
			'/admin/broke-flex-messages/campaigns',
			dto,
		)
		return data.data
	}

	static async updateCampaign(
		id: string,
		dto: UpdateBrokeFlexMessageCampaignRequest,
	): Promise<BrokeFlexMessageCampaignModel> {
		const { data } = await axiosInstance.put<BrokeFlexMessageCampaignResponse>(
			`/admin/broke-flex-messages/campaigns/${id}`,
			dto,
		)
		return data.data
	}

	static async deleteCampaign(id: string): Promise<void> {
		await axiosInstance.delete(`/admin/broke-flex-messages/campaigns/${id}`)
	}

	static async previewStage(
		campaignId: string,
		stageId: string,
	): Promise<{ success: boolean; message: string; flexMessage: any }> {
		const { data } = await axiosInstance.post(
			`/admin/broke-flex-messages/campaigns/${campaignId}/preview/${stageId}`,
		)
		return data
	}

	// Preset-based methods
	static async getPresets(): Promise<{
		success: boolean
		data: Array<{
			preset: string
			name: string
			description: string
			defaultMaxItems: number
			requiredFields: string[]
			optionalFields: string[]
		}>
	}> {
		const { data } = await axiosInstance.get(
			'/admin/broke-flex-messages/presets',
		)
		return data
	}

	static async createFromPreset(dto: {
		preset: string
		promotionIds?: string[]
		couponCodes?: string[]
		includeReturnLoss?: boolean
		includeCommission?: boolean
		includeInviteFriend?: boolean
		message: string
		name?: string
		isActive?: boolean
	}): Promise<BrokeFlexMessageCampaignModel> {
		const { data } = await axiosInstance.post<BrokeFlexMessageCampaignResponse>(
			'/admin/broke-flex-messages/campaigns/from-preset',
			dto,
		)
		return data.data
	}

	static async addStage(
		campaignId: string,
		dto: {
			minutesAfter: number
			message: string
			type: 'PROMOTION' | 'COUPON' | 'INSTANT_CREDIT'
			promotionId?: string
			couponCode?: string
		},
	): Promise<BrokeFlexMessageCampaignModel> {
		// Convert simple template format to backend format
		const backendDto: {
			minutesAfter: number
			message: string
			promotionIds?: string[]
			couponCodes?: string[]
			includeReturnLoss?: boolean
			includeCommission?: boolean
			includeInviteFriend?: boolean
			maxItemsToShow: number
		} = {
			minutesAfter: dto.minutesAfter,
			message: dto.message,
			maxItemsToShow: 1, // Always 1 item for stages 2-4
		}

		// Add the appropriate item based on type
		if (dto.type === 'PROMOTION' && dto.promotionId) {
			backendDto.promotionIds = [dto.promotionId]
		} else if (dto.type === 'COUPON' && dto.couponCode) {
			backendDto.couponCodes = [dto.couponCode]
		} else if (dto.type === 'INSTANT_CREDIT') {
			// For instant credit, enable all wallet types
			backendDto.includeReturnLoss = true
			backendDto.includeCommission = true
			backendDto.includeInviteFriend = true
		}

		const { data } = await axiosInstance.post<BrokeFlexMessageCampaignResponse>(
			`/admin/broke-flex-messages/campaigns/${campaignId}/add-stage`,
			backendDto,
		)
		return data.data
	}

	// Analytics methods
	static async getAllAnalytics(): Promise<BrokeFlexAnalyticsListResponse> {
		const { data } = await axiosInstance.get<BrokeFlexAnalyticsListResponse>(
			'/admin/broke-flex-messages/analytics',
		)
		return data
	}

	static async getBrokeFlexAnalyticsDetail(
		campaignId: string,
	): Promise<BrokeFlexAnalyticsResponse> {
		const { data } = await axiosInstance.get<BrokeFlexAnalyticsResponse>(
			`/admin/broke-flex-messages/analytics/${campaignId}`,
		)
		return data
	}
}
